import { useState, useEffect } from "react";
import { MagnifyingGlass, Lightning, Train, Bus, Leaf, ArrowRight, MapPin, Clock, CurrencyEur, CaretDown, Package, ArrowsDownUp, CheckCircle, Scales } from "@phosphor-icons/react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TIER_ICONS = { lightning: Lightning, train: Train, bus: Bus, leaf: Leaf };
const TIER_COLORS = { express: "#e74c3c", rapid: "#2563eb", standard: "var(--primary)", economico: "#27ae60" };
const TIER_BG = { express: "rgba(231,76,60,0.06)", rapid: "rgba(37,99,235,0.06)", standard: "rgba(45,74,62,0.06)", economico: "rgba(39,174,96,0.06)" };

function RouteCard({ route, onSelect }) {
  const Icon = TIER_ICONS[route.icon] || Bus;
  const color = TIER_COLORS[route.tier_id] || "var(--primary)";
  const bg = TIER_BG[route.tier_id] || "var(--bg)";
  const dep = new Date(route.departure);
  const arr = new Date(route.arrival);

  return (
    <div data-testid={`route-${route.tier_id}`} className="bento-item card-hover" style={{ overflow: "hidden" }}>
      {/* Tier header */}
      <div style={{ padding: "12px 20px", background: bg, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `2px solid ${color}20` }}>
        <div className="flex items-center gap-3">
          <div style={{ width: 36, height: 36, borderRadius: 10, background: `${color}15`, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Icon size={20} weight="fill" style={{ color }} />
          </div>
          <div>
            <span style={{ fontFamily: "Outfit", fontWeight: 700, fontSize: "1rem", color }}>{route.tier_label}</span>
            <span style={{ display: "block", fontSize: "0.7rem", color: "var(--text-muted)" }}>{route.tier_desc}</span>
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <span style={{ fontFamily: "Outfit", fontWeight: 700, fontSize: "1.5rem", color }}>{route.price_eur.toFixed(2)}</span>
          <span style={{ fontSize: "0.8rem", color: "var(--text-muted)" }}> EUR</span>
        </div>
      </div>

      {/* Route details */}
      <div style={{ padding: "16px 20px" }}>
        <div className="flex items-center justify-between mb-4">
          {/* Departure */}
          <div style={{ textAlign: "center", flex: 1 }}>
            <span style={{ fontFamily: "JetBrains Mono", fontWeight: 700, fontSize: "1.3rem", display: "block" }}>
              {dep.toLocaleString("pt-PT", { hour: "2-digit", minute: "2-digit" })}
            </span>
            <span style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>{route.origin.city}</span>
          </div>

          {/* Duration line */}
          <div style={{ flex: 2, textAlign: "center", padding: "0 16px" }}>
            <div className="flex items-center justify-center gap-1" style={{ fontSize: "0.7rem", color: "var(--text-muted)", marginBottom: 4 }}>
              <Clock size={12} /> {route.duration_hours}h
              {route.stops_count > 2 && <span> | {route.stops_count - 2} escala(s)</span>}
            </div>
            <div style={{ position: "relative", height: 2, background: "var(--border-color)", borderRadius: 1 }}>
              <div style={{ position: "absolute", left: 0, top: -3, width: 8, height: 8, borderRadius: "50%", background: color }} />
              {route.stops.filter(s => s.type === "transfer").map((s, i) => (
                <div key={`xfer-${s.city}-${i}`} style={{ position: "absolute", left: `${30 + i * 20}%`, top: -2, width: 6, height: 6, borderRadius: "50%", background: "var(--border-color)", border: `2px solid ${color}` }} />
              ))}
              <div style={{ position: "absolute", right: 0, top: -3, width: 8, height: 8, borderRadius: "50%", background: color }} />
            </div>
            <div style={{ fontSize: "0.65rem", color: "var(--text-muted)", marginTop: 4 }}>
              {route.distance_km} km | {route.stops_count > 2 ? "Com escalas" : "Direto"}
            </div>
          </div>

          {/* Arrival */}
          <div style={{ textAlign: "center", flex: 1 }}>
            <span style={{ fontFamily: "JetBrains Mono", fontWeight: 700, fontSize: "1.3rem", display: "block" }}>
              {arr.toLocaleString("pt-PT", { hour: "2-digit", minute: "2-digit" })}
            </span>
            <span style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>{route.destination.city}</span>
          </div>
        </div>

        {/* Stops timeline */}
        {route.stops_count > 2 && (
          <div style={{ padding: "10px 12px", borderRadius: 10, background: "var(--bg)", marginBottom: 12, fontSize: "0.75rem" }}>
            {route.stops.map((s, i) => (
              <span key={`stop-${s.city}-${i}`} className="inline-flex items-center gap-1" style={{ color: s.type === "transfer" ? "var(--warning)" : "var(--primary)", fontWeight: 600 }}>
                {s.city}
                {i < route.stops.length - 1 && <ArrowRight size={10} style={{ color: "var(--text-muted)", margin: "0 4px" }} />}
              </span>
            ))}
          </div>
        )}

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3" style={{ fontSize: "0.7rem", color: "var(--text-muted)" }}>
            <span className="flex items-center gap-1"><Leaf size={12} style={{ color: "#27ae60" }} /> -{route.co2_saved_kg}kg CO2</span>
          </div>
          <button
            data-testid={`select-route-${route.tier_id}`}
            onClick={() => onSelect(route)}
            style={{ padding: "8px 24px", borderRadius: 9999, border: "none", background: color, color: "white", fontFamily: "Outfit", fontWeight: 600, fontSize: "0.85rem", cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}
          >
            Selecionar <ArrowRight size={14} weight="bold" />
          </button>
        </div>
      </div>
    </div>
  );
}

export default function RouteSearch() {
  const [points, setPoints] = useState([]);
  const [originId, setOriginId] = useState("");
  const [destId, setDestId] = useState("");
  const [weight, setWeight] = useState(0.5);
  const [bagType, setBagType] = useState("standard");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [popularRoutes, setPopularRoutes] = useState([]);
  const [selectedRoute, setSelectedRoute] = useState(null);

  useEffect(() => {
    axios.get(`${API}/points`).then(r => setPoints(r.data));
    axios.get(`${API}/routes/popular`).then(r => setPopularRoutes(r.data)).catch(() => {});
  }, []);

  const handleSearch = async () => {
    if (!originId || !destId) return;
    setLoading(true);
    setResults(null);
    setSelectedRoute(null);
    try {
      const res = await axios.post(`${API}/routes/search`, { origin_point_id: originId, destination_point_id: destId, weight_kg: weight, bag_type: bagType });
      setResults(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const swapPoints = () => { const t = originId; setOriginId(destId); setDestId(t); };

  const selectStyle = { width: "100%", padding: "14px 16px 14px 42px", borderRadius: 14, border: "1px solid var(--border-color)", fontFamily: "Manrope", fontSize: "0.9rem", background: "var(--surface)", outline: "none", appearance: "none" };

  return (
    <div data-testid="route-search" className="pt-20 px-6 pb-12">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8 animate-fade-in-up">
          <h1 style={{ fontFamily: "Outfit", fontWeight: 700, fontSize: "1.8rem", marginBottom: 4 }}>Pesquisar entregas</h1>
          <p style={{ color: "var(--text-muted)", fontSize: "0.95rem" }}>Encontre a melhor rota e preco para o seu envio</p>
        </div>

        {/* Search form */}
        <div className="bento-item p-6 mb-6 animate-fade-in-up stagger-1" data-testid="route-search-form">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
            <div className="md:col-span-4" style={{ position: "relative" }}>
              <label style={{ display: "block", fontSize: "0.65rem", fontWeight: 600, color: "var(--text-muted)", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.05em" }}>Origem</label>
              <MapPin size={18} style={{ position: "absolute", left: 14, bottom: 14, color: "var(--success)" }} />
              <select data-testid="route-origin-select" value={originId} onChange={e => setOriginId(e.target.value)} style={selectStyle}>
                <option value="">Ponto de recolha</option>
                {points.map(p => <option key={p.id} value={p.id}>{p.name} - {p.city}</option>)}
              </select>
            </div>

            <div className="md:col-span-1 flex justify-center">
              <button data-testid="swap-points-btn" onClick={swapPoints} style={{ width: 40, height: 40, borderRadius: "50%", border: "1px solid var(--border-color)", background: "var(--surface)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <ArrowsDownUp size={18} style={{ color: "var(--primary)" }} />
              </button>
            </div>

            <div className="md:col-span-4" style={{ position: "relative" }}>
              <label style={{ display: "block", fontSize: "0.65rem", fontWeight: 600, color: "var(--text-muted)", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.05em" }}>Destino</label>
              <MapPin size={18} style={{ position: "absolute", left: 14, bottom: 14, color: "var(--accent)" }} />
              <select data-testid="route-dest-select" value={destId} onChange={e => setDestId(e.target.value)} style={selectStyle}>
                <option value="">Ponto de entrega</option>
                {points.map(p => <option key={p.id} value={p.id}>{p.name} - {p.city}</option>)}
              </select>
            </div>

            <div className="md:col-span-3">
              <button data-testid="search-routes-btn" onClick={handleSearch} disabled={!originId || !destId || loading} style={{ width: "100%", padding: "14px 24px", borderRadius: 14, border: "none", background: !originId || !destId ? "var(--border-color)" : "var(--primary)", color: "white", fontFamily: "Outfit", fontWeight: 600, fontSize: "0.95rem", cursor: !originId || !destId ? "default" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, opacity: loading ? 0.6 : 1 }}>
                <MagnifyingGlass size={18} weight="bold" /> {loading ? "A pesquisar..." : "Pesquisar"}
              </button>
            </div>
          </div>

          {/* Weight + bag type */}
          <div className="grid grid-cols-2 gap-4 mt-4">
            <div>
              <label style={{ display: "block", fontSize: "0.65rem", fontWeight: 600, color: "var(--text-muted)", marginBottom: 4, textTransform: "uppercase" }}>Peso (kg)</label>
              <input type="number" step="0.1" min="0.1" max="10" value={weight} onChange={e => setWeight(parseFloat(e.target.value) || 0.5)} style={{ width: "100%", padding: "10px 14px", borderRadius: 10, border: "1px solid var(--border-color)", fontFamily: "Manrope", fontSize: "0.85rem", background: "var(--bg)", outline: "none" }} />
            </div>
            <div>
              <label style={{ display: "block", fontSize: "0.65rem", fontWeight: 600, color: "var(--text-muted)", marginBottom: 4, textTransform: "uppercase" }}>Tipo de bolsa</label>
              <select value={bagType} onChange={e => setBagType(e.target.value)} style={{ width: "100%", padding: "10px 14px", borderRadius: 10, border: "1px solid var(--border-color)", fontFamily: "Manrope", fontSize: "0.85rem", background: "var(--bg)", outline: "none", appearance: "none" }}>
                <option value="standard">Standard</option>
                <option value="padded">Almofadada</option>
                <option value="large">Grande</option>
                <option value="fragile">Fragil</option>
              </select>
            </div>
          </div>
        </div>

        {/* Results */}
        {results && (
          <div className="animate-fade-in-up" data-testid="route-results">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 style={{ fontFamily: "Outfit", fontWeight: 700, fontSize: "1.2rem" }}>{results.origin} → {results.destination}</h2>
                <span style={{ fontSize: "0.85rem", color: "var(--text-muted)" }}>{results.distance_km} km | {results.routes.length} opcoes disponiveis</span>
              </div>
            </div>
            <div className="flex flex-col gap-4">
              {results.routes.map(r => (
                <RouteCard key={r.tier_id} route={r} onSelect={setSelectedRoute} />
              ))}
            </div>
          </div>
        )}

        {/* Selected route confirmation */}
        {selectedRoute && (
          <div className="bento-item p-6 mt-6 animate-fade-in" style={{ border: `2px solid ${TIER_COLORS[selectedRoute.tier_id]}` }} data-testid="route-selected">
            <div className="flex items-center gap-3 mb-4">
              <CheckCircle size={24} weight="fill" style={{ color: "var(--success)" }} />
              <span style={{ fontFamily: "Outfit", fontWeight: 700, fontSize: "1.1rem" }}>Rota selecionada: {selectedRoute.tier_label}</span>
            </div>
            <p style={{ color: "var(--text-muted)", fontSize: "0.9rem" }}>
              {selectedRoute.origin.city} → {selectedRoute.destination.city} | {selectedRoute.duration_hours}h | {selectedRoute.price_eur.toFixed(2)} EUR
            </p>
            <p style={{ color: "var(--text-muted)", fontSize: "0.8rem", marginTop: 4 }}>Prossiga para "Envios" para completar o envio com esta rota.</p>
          </div>
        )}

        {/* Popular routes (when no search) */}
        {!results && popularRoutes.length > 0 && (
          <div className="animate-fade-in-up stagger-2" data-testid="popular-routes">
            <h3 style={{ fontFamily: "Outfit", fontWeight: 600, fontSize: "1rem", marginBottom: 12 }}>Rotas populares</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {popularRoutes.map((r, i) => (
                <div key={`pop-${r.origin}-${r.destination}-${i}`} className="bento-item p-4 card-hover cursor-pointer" onClick={() => {
                  const o = points.find(p => p.city === r.origin);
                  const d = points.find(p => p.city === r.destination);
                  if (o && d) { setOriginId(o.id); setDestId(d.id); }
                }}>
                  <div className="flex items-center justify-between">
                    <div>
                      <span style={{ fontWeight: 600, fontSize: "0.9rem" }}>{r.origin}</span>
                      <ArrowRight size={12} style={{ margin: "0 6px", color: "var(--text-muted)" }} />
                      <span style={{ fontWeight: 600, fontSize: "0.9rem" }}>{r.destination}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 mt-2" style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>
                    <span>{r.distance_km} km</span>
                    <span>~{r.avg_hours}h</span>
                    <span style={{ marginLeft: "auto", fontWeight: 700, color: "var(--primary)" }}>desde {r.from_price.toFixed(2)} EUR</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
